import { portalText } from "../i18n/portal.js";
/**
 * The workspace the person is signed in to, as the workspace listing describes it
 * (customer-portal review 2026-09-25 #150).
 *
 * The sign-in names the workspace by id only. Its name, initials and figures are the listing's,
 * read on their own: when the sign-in read carried them, a failed listing failed the sign-in too,
 * and "This page could not load" replaced the sidebar and the top bar. Now a failed listing leaves
 * the shell standing; the shell says the workspace could not be read, with the request id and
 * Try again, and the page below it loads.
 */
import { useQuery } from "@tanstack/react-query";

import { apiProblemText } from "../api/api-error.js";
import type { WorkspaceView } from "../api/gateway.js";
import { useGateway } from "../api/provider.js";
import { authSessionQuery, workspaceQuery } from "../api/query.js";

export interface CurrentWorkspace {
  /** The signed-in workspace, once the listing has it. `null` while it is read, or when it failed. */
  readonly workspace: WorkspaceView | null;
  /** Why it could not be read, in plain words (with the request id for a 5xx), or `null`. */
  readonly problem: string | null;
  /** Reads the listing again. */
  readonly retry: () => void;
}

export function useCurrentWorkspace(): CurrentWorkspace {
  const gateway = useGateway();
  const authSession = useQuery(authSessionQuery(gateway));
  const workspaceId = authSession.data?.workspaceId ?? null;
  // With the organization alone open there is no workspace to describe, and no listing to read
  // (plan-014 A4): `workspace` and `problem` are both `null` then.
  const listing = useQuery({ ...workspaceQuery(gateway), enabled: workspaceId !== null });

  const found = listing.data?.find((one) => one.workspaceId === workspaceId) ?? null;
  let problem: string | null = null;
  if (found === null && workspaceId !== null && listing.isError) {
    problem = apiProblemText(listing.error, portalText("shell", "your_workspaces_could_not_be_read_a10ec2da"));
  } else if (found === null && listing.data !== undefined && workspaceId !== null) {
    problem = "This workspace is not in your account's list of workspaces.";
  }

  return {
    workspace: found,
    problem,
    retry: () => {
      void listing.refetch();
    },
  };
}
